import { useTranslation } from "react-i18next";

import { DOMAINS } from "../framework/data";
import type { DomainId } from "../framework/types";
import type { ReportAggregates } from "./reportTypes";
import { EntryExitBars } from "./components/EntryExitBars";
import { ChangeAnalysis } from "./components/ChangeAnalysis";
import { TransitionMatrix } from "./components/TransitionMatrix";
import { useLabels } from "./useLabels";

/**
 * Domain view — the six framework domains. An overview grid comparing all six
 * entry→exit, then per-domain detail: roll-up, analysis of change, and the
 * entry×exit transition matrix for each subdomain in the domain. Mirrors the
 * Domain view sheet of the source workbook.
 */
type Props = { agg: ReportAggregates };

export function DomainView({ agg }: Props) {
  const { t } = useTranslation();
  const labels = useLabels();
  const domainAgg = (id: DomainId) =>
    agg.byDomain.find((d) => d.domainId === id);

  return (
    <div className="rpt-view">
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">{t("reports.domain.overview")}</h2>
        <p className="rpt-panel__lead">{t("reports.domain.overviewLead")}</p>
        <div className="rpt-pou-grid">
          {DOMAINS.map((domain) => {
            const d = domainAgg(domain.id);
            if (!d) return null;
            return (
              <div key={domain.id} className="rpt-pou-card">
                <h3 className="rpt-pou-card__name">
                  {labels.domain(domain.id)}
                </h3>
                <EntryExitBars data={d} compact />
              </div>
            );
          })}
        </div>
      </section>

      {DOMAINS.map((domain) => {
        const d = domainAgg(domain.id);
        if (!d) return null;
        const subs = agg.bySubdomain.filter(
          (s) => s.domainId === domain.id && s.transition.total > 0,
        );
        return (
          <section key={domain.id} className="rpt-panel">
            <h2 className="rpt-panel__title">{labels.domain(domain.id)}</h2>
            <div className="rpt-pou-detail">
              <div className="rpt-pou-detail__rollup">
                <h3 className="rpt-subhead">{t("reports.domain.rollup")}</h3>
                <EntryExitBars data={d} />
              </div>
              <div className="rpt-pou-detail__change">
                <h3 className="rpt-subhead">{t("reports.analysisOfChange")}</h3>
                <ChangeAnalysis analysis={d.analysis} />
              </div>
            </div>
            {subs.length > 0 && (
              <>
                <h3 className="rpt-subhead">{t("reports.pou.transitions")}</h3>
                <div className="rpt-matrix-grid">
                  {subs.map((s) => (
                    <TransitionMatrix
                      key={s.subdomainId}
                      transition={s.transition}
                      caption={labels.subdomain(s.subdomainId)}
                    />
                  ))}
                </div>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default DomainView;
